import { useEffect, useMemo, useState } from "react";
import { getProjects, getFiles, getFile } from "./api";
import "./App.css";

const ABOUT_KEY = "__about__";

function getParam(name) {
  const u = new URL(window.location.href);
  return u.searchParams.get(name) || "";
}

function setParams(project, file) {
  const u = new URL(window.location.href);
  if (project) u.searchParams.set("project", project);
  else u.searchParams.delete("project");

  if (file) u.searchParams.set("file", file);
  else u.searchParams.delete("file");

  window.history.replaceState({}, "", u.toString());
}

const ABOUT_PARAGRAPHS = [
  "Profissional de Qualidade de Software com atuação em QA Manual, Automação e QA Security.",
  "Experiência em validação funcional, testes de integração, regressão, smoke e análise de critérios de aceite, garantindo aderência a requisitos funcionais e não funcionais.",
  "Atuação técnica com testes baseados em evidência, leitura estrutural de código e execução via CLI em ambientes Linux.",
  "Foco em confiabilidade, rastreabilidade, redução de riscos e prevenção de falhas ao longo de todo o ciclo de desenvolvimento."
];

export default function App() {
  const [projects, setProjects] = useState([]);
  const [files, setFiles] = useState([]);
  const [content, setContent] = useState("");

  const [selectedProject, setSelectedProject] = useState(getParam("project"));
  const [selectedFile, setSelectedFile] = useState(getParam("file"));

  const hasOpen = useMemo(
    () => Boolean(content && selectedProject && selectedFile),
    [content, selectedProject, selectedFile]
  );

  const openAbout = () => {
    setSelectedProject(ABOUT_KEY);
    setSelectedFile("ABOUT");
    setFiles([]);
    setContent("");
    setParams(ABOUT_KEY, "ABOUT");
  };

  const openFile = (p, f) => {
    setSelectedProject(p);
    setSelectedFile(f);
    setParams(p, f);
    getFile(p, f).then(setContent);
  };

  const openProject = (p) => {
    setSelectedProject(p);
    setSelectedFile("");
    setContent("");
    setParams(p, "");
    getFiles(p).then((fs) => {
      setFiles(fs);
      const preferred =
        fs.find((x) => x === "00_TECHNICAL_REPORT.md") ||
        fs.find((x) => x.toLowerCase() === "readme.md") ||
        fs[0];
      if (preferred) openFile(p, preferred);
    });
  };

  const closeViewer = () => {
    setSelectedFile("");
    setContent("");
    setParams(selectedProject, "");
  };

  useEffect(() => {
    getProjects().then((ps) => {
      setProjects(ps);

      const urlProject = selectedProject;
      const urlFile = selectedFile;

      // Se já tem estado na URL, respeita (refresh mantém o que estava aberto)
      if (urlProject && urlFile) {
        if (urlProject === ABOUT_KEY) {
          openAbout();
          return;
        }
        setSelectedProject(urlProject);
        getFiles(urlProject).then((fs) => {
          setFiles(fs);
          openFile(urlProject, urlFile);
        });
        return;
      }

      // Sem estado na URL: abre SOBRE por padrão
      openAbout();
    });
  }, []);

  const renderContent = () => {
    // SOBRE
    if (selectedProject === ABOUT_KEY) {
      return (
        <div className="about">
          <div className="aboutTitle">Sobre</div>
          {ABOUT_PARAGRAPHS.map((p, i) => (
            <p key={i} className="aboutP">{p}</p>
          ))}
        </div>
      );
    }

    // Reports
    if (!content) return <div className="empty">Select a file to view.</div>;

    const lines = content.split("\n");
    return (
      <div className="text">
        {lines.map((line, idx) => {
          const m = line.match(/^(Evidence:\s*)(.+)$/i);
          if (m && selectedProject) {
            const prefix = m[1];
            const path = m[2].trim();
            return (
              <div key={idx} className="line">
                <span>{prefix}</span>
                <span className="link" onClick={() => openFile(selectedProject, path)}>
                  {path}
                </span>
              </div>
            );
          }
          return (
            <div key={idx} className="line">
              {line}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="brand">
          <div className="name">Pablo Seixas</div>
          <div className="role">QA Engineering</div>
        </div>

        <div className="sectionTitle">About</div>
        <div className="list">
          <div
            onClick={openAbout}
            className={"item " + (selectedProject === ABOUT_KEY ? "active" : "")}
          >
            SOBRE
          </div>
        </div>

        <div className="sectionTitle">Projects</div>
        <div className="list">
          {projects.map((p) => (
            <div
              key={p}
              onClick={() => openProject(p)}
              className={"item " + (p === selectedProject ? "active" : "")}
            >
              {p}
            </div>
          ))}
        </div>

        <div className="sectionTitle">Reports</div>
        <div className="list">
          {selectedProject === ABOUT_KEY && (
            <div className="muted">Select a project to view reports</div>
          )}
          {selectedProject !== ABOUT_KEY && files.length === 0 && (
            <div className="muted">No files</div>
          )}
          {selectedProject !== ABOUT_KEY &&
            files.map((f) => (
              <div
                key={f}
                onClick={() => openFile(selectedProject, f)}
                className={"item file " + (f === selectedFile ? "active" : "")}
              >
                {f}
              </div>
            ))}
        </div>
      </aside>

      <main className="viewer">
        <div className="viewerTop">
          <div className="viewerTitle">
            {selectedProject === ABOUT_KEY ? "About" : selectedProject ? selectedProject : "Portal"}
            {selectedFile && selectedProject !== ABOUT_KEY ? ` / ${selectedFile}` : ""}
          </div>

          {hasOpen && selectedProject !== ABOUT_KEY && (
            <button className="btn" onClick={closeViewer}>
              Close
            </button>
          )}
        </div>

        <div className="viewerBody">{renderContent()}</div>
      </main>
    </div>
  );
}
